import React, {useState, useEffect, useMemo} from 'react';
import {ActivityIndicator, TouchableOpacity, View, TextInput, Text} from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import Ionicons from 'react-native-vector-icons/Ionicons';
import {Chess, validateFen} from 'chess.js';
import {Chessboard} from 'react-chessboard';
import SlideModal from '@/Atoms/SlideModal';
import Loader from '@/Atoms/CCLoader/CCLoader';
import useCreateStudyGame from '@/Hooks/CreateNewStudyApi/useCreateStudyGame';
import useCreateNewChapter from '@/Hooks/CreateNewChapterApi/useCreateNewChapter';
import BoardEditor from '@/Components/BoardEditor';
import {useNavigation} from '@react-navigation/native';
import {useToast} from '@/toast/useToast';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import {ScreenName} from '@/navigators/StackConstants';
import {ensureBlankLineAfterHeaders} from '@/Utils/CommonUtils';
import {newChapterDetailsType} from './types';

const START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

function splitPgn(pgnText: string): string[] {
  const games: string[] = [];
  const lines = pgnText.replace(/\r\n/g, '\n').split('\n');
  let currentGame: string[] = [];
  let inMoveSection = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Check if it is a valid PGN tag line: [Key "Value"]
    const isTagLine = /^\[[A-Za-z0-9_]+\s+"[^"]*"\]$/.test(trimmed);

    if (isTagLine) {
      if (inMoveSection) {
        const gameStr = currentGame.join('\n').trim();
        if (gameStr) {
          games.push(gameStr);
        }
        currentGame = [];
        inMoveSection = false;
      }
    } else if (trimmed !== '') {
      inMoveSection = true;
    }

    currentGame.push(line);
  }

  if (currentGame.length > 0) {
    const gameStr = currentGame.join('\n').trim();
    if (gameStr) {
      games.push(gameStr);
    }
  }

  return games;
}

function getPgnGameName(
  gamePgn: string,
  index: number,
  fallbackBase: string,
): string {
  const getHeader = (key: string) => {
    const match = gamePgn.match(new RegExp(`\\[${key}\\s+"([^"]+)"\\]`, 'i'));
    return match ? match[1].trim() : null;
  };

  const event = getHeader('Event');
  const white = getHeader('White');
  const black = getHeader('Black');

  const hasWhite = white && white !== '?';
  const hasBlack = black && black !== '?';

  if (hasWhite && hasBlack) {
    return `${white} vs ${black}`;
  }
  if (hasWhite) {
    return white;
  }
  if (hasBlack) {
    return black;
  }

  if (
    event &&
    event !== '?' &&
    event !== 'Lichess Study' &&
    event !== 'Lichess Game'
  ) {
    return event;
  }

  return fallbackBase
    ? `${fallbackBase} - Game ${index + 1}`
    : `Game ${index + 1}`;
}

function replaceEventHeader(pgn: string, newName: string): string {
  const eventRegex = /^\[Event\s+"[^"]*"\]/im;
  if (eventRegex.test(pgn)) {
    return pgn.replace(eventRegex, `[Event "${newName}"]`);
  } else {
    return `[Event "${newName}"]\n${pgn}`;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Main CreateNewStudy Component (Popup 1)
// ─────────────────────────────────────────────────────────────────────────────
function CreateNewStudy({
  createChapterOnly = false,
  successCallback,
  overrideButton,
  studySlug,
  setNewChapterCreated,
  isStudyModalOpen,
  setIsStudyModalOpen,
}: {
  createChapterOnly?: boolean;
  successCallback?: () => void;
  overrideButton?: (func: () => void) => any;
  studySlug?: string;
  setNewChapterCreated?: (func: () => void) => any;
  isStudyModalOpen: boolean;
  setIsStudyModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const navigation = useNavigation();
  const showToast = useToast();
  const {isMobileOrTab} = useMediaQuery();
  const [openNewStudyModal, setOpenNewStudyModal] = useState(false);
  const [isBoardEditorVisible, setIsBoardEditorVisible] = useState(false);
  const [error, setError] = useState({flag: false, errorMsg: ''});

  const [newChapterDetails, setNewChapterDetails] = useState<newChapterDetailsType>({
    name: '',
    types: {key: 'Start', value: 'Start'}, // Default to start position
    fen: START_FEN,
    pgn: '',
  });

  const [orientation, setOrientation] = useState<'White' | 'Black'>('White');
  const [newStudyDetails, setNewStudyDetails] = useState<{
    name: string;
    chapterName: string;
    pgn: string;
    type:
      | {key: 'FEN'; value: 'FEN'}
      | {key: 'PGN'; value: 'PGN'}
      | {key: 'Start'; value: 'Start'}
      | null;
  }>({
    name: '',
    pgn: '',
    type: null,
  });

  useEffect(() => {
    setOpenNewStudyModal(isStudyModalOpen);
  }, [isStudyModalOpen]);

  const {
    mutateAsync: createNewStudyAsync,
    data: newStudySuccess,
    error: newStudyError,
  } = useCreateStudyGame();

  const {
    mutateAsync: createNewChapterAsync,
    data: newChapterSuccess,
    error: newChapterError,
  } = useCreateNewChapter();

  const [loaderState, setLoaderState] = useState(false);

  useEffect(() => {
    if (newStudyError || newChapterError) {
      setError({flag: true, errorMsg: ''});
      setLoaderState(false);
    }
  }, [newStudyError, newChapterError]);

  useEffect(() => {
    if (newStudySuccess?.success) {
      handleNewStudy(newStudySuccess);
      setLoaderState(false);
    } else if (newChapterSuccess?.success) {
      successCallback?.();
      setLoaderState(false);
      setNewChapterCreated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newStudySuccess, newChapterSuccess]);

  const handleNewStudy = newGameDetails => {
    const studyGameSlug = newGameDetails?.data?.slug;
    navigation.navigate(ScreenName.StudyDetails, {
      study_id: studyGameSlug,
    });
  };

  const resetStates = () => {
    setOpenNewStudyModal(false);
    setIsStudyModalOpen(false);
    setError({flag: false, errorMsg: ''});
    setNewChapterDetails({
      name: '',
      types: {key: 'Start', value: 'Start'},
      fen: START_FEN,
      pgn: '',
    });
    setOrientation('White');
    setNewStudyDetails({
      name: '',
      pgn: '',
      type: null,
    });
  };

  const handleFenSubmit = async () => {
    try {
      setLoaderState(true);
      setError({flag: false, errorMsg: ''});

      const typesKey = newChapterDetails?.types?.key;

      if (typesKey === 'FEN' || typesKey === 'Editor') {
        const validatePiece = validateFen(newChapterDetails?.fen);
        if (!validatePiece?.ok) {
          throw new Error(validatePiece?.error);
        }

        const startingPos = {fen: newChapterDetails?.fen};
        if (createChapterOnly) {
          await createNewChapterAsync({
            name: newChapterDetails?.name,
            ...startingPos,
            orientation: orientation === 'Black' ? 'B' : 'W',
            slug: studySlug,
          });
          successCallback?.();
          setLoaderState(false);
          setNewChapterCreated(true);
          resetStates();
        } else {
          const res = await createNewStudyAsync({
            name: newStudyDetails?.name,
            chapter: {
              name: newChapterDetails?.name,
              ...startingPos,
              orientation: orientation === 'Black' ? 'B' : 'W',
            },
          });
          if (res?.success) {
            handleNewStudy(res);
          }
          setLoaderState(false);
          resetStates();
        }
      } else if (typesKey === 'PGN') {
        const pgn = newChapterDetails?.pgn;
        if (!pgn || typeof pgn !== 'string') {
          throw new Error('PGN is empty or not a string.');
        }

        const games = splitPgn(pgn);
        if (games.length === 0) {
          throw new Error('No valid games found in PGN.');
        }

        if (createChapterOnly) {
          const failedGameNumbers: number[] = [];
          for (let i = 0; i < games.length; i++) {
            const gamePgn = games[i];
            const formattedPgn = ensureBlankLineAfterHeaders(gamePgn);

            try {
              new Chess().loadPgn(formattedPgn);
            } catch (err) {
              console.error(`Failed parsing game ${i + 1}:`, err);
              failedGameNumbers.push(i + 1);
              continue;
            }

            const chapterName = getPgnGameName(formattedPgn, i, newChapterDetails?.name);
            const pgnWithEventName = replaceEventHeader(formattedPgn, chapterName);

            await createNewChapterAsync({
              name: chapterName,
              pgn: pgnWithEventName,
              orientation: orientation === 'Black' ? 'B' : 'W',
              slug: studySlug,
            });
          }
          if (failedGameNumbers.length === games.length) {
            throw new Error('None of the games could be parsed as valid PGN.');
          }
          successCallback?.();
          setLoaderState(false);
          setNewChapterCreated(true);
          resetStates();
          if (failedGameNumbers.length > 0) {
            showToast(
              `${failedGameNumbers.length} of ${games.length} game(s) skipped due to invalid PGN`,
              {type: 'Error'},
            );
          }
        } else {
          const firstGamePgn = games[0];
          const formattedFirstPgn = ensureBlankLineAfterHeaders(firstGamePgn);
          try {
            new Chess().loadPgn(formattedFirstPgn);
          } catch {
            throw new Error('The first game could not be parsed as valid PGN.');
          }
          const firstChapterName = getPgnGameName(
            formattedFirstPgn,
            0,
            newChapterDetails?.name || newStudyDetails?.name,
          );
          const pgnWithFirstEvent = replaceEventHeader(formattedFirstPgn, firstChapterName);

          const res = await createNewStudyAsync({
            name: newStudyDetails?.name,
            chapter: {
              name: firstChapterName,
              pgn: pgnWithFirstEvent,
              orientation: orientation === 'Black' ? 'B' : 'W',
            },
          });

          const studyGameSlug = res?.data?.slug;
          const failedGameNumbers: number[] = [];
          if (studyGameSlug) {
            for (let i = 1; i < games.length; i++) {
              const gamePgn = games[i];
              const formattedPgn = ensureBlankLineAfterHeaders(gamePgn);

              try {
                new Chess().loadPgn(formattedPgn);
              } catch (err) {
                console.error(`Failed parsing game ${i + 1}:`, err);
                failedGameNumbers.push(i + 1);
                continue;
              }

              const chapterName = getPgnGameName(
                formattedPgn,
                i,
                newChapterDetails?.name || newStudyDetails?.name,
              );
              const pgnWithEventName = replaceEventHeader(formattedPgn, chapterName);

              await createNewChapterAsync({
                name: chapterName,
                pgn: pgnWithEventName,
                orientation: orientation === 'Black' ? 'B' : 'W',
                slug: studyGameSlug,
              });
            }
            handleNewStudy(res);
          }
          setLoaderState(false);
          resetStates();
          if (failedGameNumbers.length > 0) {
            showToast(
              `${failedGameNumbers.length} of ${games.length - 1} additional game(s) skipped due to invalid PGN`,
              {type: 'Error'},
            );
          }
        }
      } else {
        const startingPos = {fen: START_FEN};
        if (createChapterOnly) {
          await createNewChapterAsync({
            name: newChapterDetails?.name,
            ...startingPos,
            orientation: orientation === 'Black' ? 'B' : 'W',
            slug: studySlug,
          });
          successCallback?.();
          setLoaderState(false);
          setNewChapterCreated(true);
          resetStates();
        } else {
          const res = await createNewStudyAsync({
            name: newStudyDetails?.name,
            chapter: {
              name: newChapterDetails?.name,
              ...startingPos,
              orientation: orientation === 'Black' ? 'B' : 'W',
            },
          });
          if (res?.success) {
            handleNewStudy(res);
          }
          setLoaderState(false);
          resetStates();
        }
      }
    } catch (err) {
      setLoaderState(false);
      setError({flag: true, errorMsg: `${err}`});
    }
  };

  const nameLen = newStudyDetails?.name?.length || 0;
  const showNameHint = !createChapterOnly && nameLen > 0 && nameLen < 4;
  const typeKey = newChapterDetails?.types?.key;

  const canSubmit =
    (createChapterOnly || nameLen >= 4) &&
    !!newChapterDetails?.name &&
    !(typeKey === 'FEN' && !newChapterDetails?.fen) &&
    !(typeKey === 'Editor' && !newChapterDetails?.fen) &&
    !(typeKey === 'PGN' && !newChapterDetails?.pgn);

  const previewDescription = useMemo(() => {
    switch (typeKey) {
      case 'FEN':
        return 'Custom FEN position';
      case 'Editor':
        return 'Custom board position';
      case 'PGN':
        return 'PGN game moves';
      default:
        return 'Standard starting position';
    }
  }, [typeKey]);

  return (
    <>
      <Loader loading={loaderState} />
      
      {/* ── Popup 1: New Workbook/Chapter Modal ─────────────────────────────── */}
      <SlideModal
        visible={openNewStudyModal}
        onClose={resetStates}
        type="center"
        showHeader={false}
        contentPadding={0}
        customStyle={{
          borderRadius: 20,
          overflow: 'hidden',
          backgroundColor: '#FFFFFF',
          width: 660,
          maxWidth: '92%',
          alignSelf: 'center',
          borderWidth: 0,
          shadowColor: '#000',
          shadowOffset: {width: 0, height: 10},
          shadowOpacity: 0.08,
          shadowRadius: 20,
          elevation: 5,
        }}>
        
        {/* Header Block */}
        <View
          style={{
            backgroundColor: '#2C1A11',
            paddingHorizontal: 24,
            paddingVertical: 18,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
          }}>
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, marginRight: 24}}>
            <View
              style={{
                width: 38,
                height: 38,
                borderRadius: 8,
                backgroundColor: '#402C1F',
                borderWidth: 1,
                borderColor: '#B58E58',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <MaterialCommunityIcons name="chess-king" size={18} color="#FFFFFF" />
            </View>
            <View style={{flex: 1}}>
              <Text style={{fontSize: 15, fontWeight: 'bold', color: '#FFFFFF', letterSpacing: -0.2}}>
                {createChapterOnly ? 'New Chapter' : 'New Workbook'}
              </Text>
              <Text style={{fontSize: 11, color: '#A39081', marginTop: 1}}>
                {createChapterOnly
                  ? 'A fresh board, appended after your existing chapters.'
                  : "Name it, set the position, and you're in."}
              </Text>
            </View>
          </View>

          {/* Close button */}
          <TouchableOpacity
            onPress={resetStates}
            activeOpacity={0.8}
            style={{
              width: 28,
              height: 28,
              borderRadius: 14,
              backgroundColor: 'rgba(255,255,255,0.08)',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Ionicons name="close-outline" size={16} color="#FFFFFF" />
          </TouchableOpacity>
        </View>

        {/* Body columns */}
        <View style={{flexDirection: 'row', width: '100%'}}>
          {/* Left Column: Form Fields */}
          <View style={{flex: 1.2, padding: 24, gap: 14}}>
            {/* Inputs row */}
            <View style={{flexDirection: 'row', gap: 10, width: '100%'}}>
              {!createChapterOnly && (
                <View style={{flex: 1, gap: 6}}>
                  <Text style={{fontSize: 10, fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>WORKBOOK NAME</Text>
                  <TextInput
                    value={newStudyDetails?.name}
                    placeholder="e.g. Sicilian Najdorf"
                    placeholderTextColor="#A39081"
                    onChangeText={text => setNewStudyDetails(prev => ({...prev, name: text}))}
                    style={{
                      height: 38,
                      borderRadius: 8,
                      borderWidth: 1,
                      borderColor: showNameHint ? '#FF4D4D' : '#E6E6E6',
                      backgroundColor: '#FFFFFF',
                      paddingHorizontal: 12,
                      fontSize: 12,
                      color: '#1E110A',
                    }}
                  />
                  {showNameHint && (
                    <Text style={{fontSize: 9, color: '#FF4D4D', fontWeight: '500'}}>Minimum 4 characters required</Text>
                  )}
                </View>
              )}
              <View style={{flex: 1, gap: 6}}>
                <Text style={{fontSize: 10, fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>CHAPTER NAME</Text>
                <TextInput
                  value={newChapterDetails?.name}
                  placeholder="e.g. Main line"
                  placeholderTextColor="#A39081"
                  onChangeText={text => setNewChapterDetails(prev => ({...prev, name: text}))}
                  style={{
                    height: 38,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: '#E6E6E6',
                    backgroundColor: '#FFFFFF',
                    paddingHorizontal: 12,
                    fontSize: 12,
                    color: '#1E110A',
                  }}
                />
              </View>
            </View>

            {/* Orientation */}
            <View style={{gap: 6}}>
              <Text style={{fontSize: 10, fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>ORIENTATION</Text>
              <View style={{flexDirection: 'row', backgroundColor: '#F5F5F5', borderRadius: 8, padding: 3, height: 36}}>
                {(['White', 'Black'] as const).map(o => (
                  <TouchableOpacity
                    key={o}
                    onPress={() => setOrientation(o)}
                    style={{
                      flex: 1,
                      borderRadius: 6,
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: orientation === o ? '#FFFFFF' : 'transparent',
                      shadowColor: orientation === o ? '#000' : 'transparent',
                      shadowOffset: {width: 0, height: 1},
                      shadowOpacity: 0.15,
                      shadowRadius: 2,
                      elevation: 1,
                    }}>
                    <Text style={{fontSize: 12, fontWeight: orientation === o ? 'bold' : 'normal', color: orientation === o ? '#1E110A' : '#757575'}}>{o}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Position Selector cards */}
            <View style={{gap: 6}}>
              <Text style={{fontSize: 10, fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>POSITION</Text>
              <View style={{flexDirection: 'row', justifyContent: 'space-between', gap: 6}}>
                {[
                  {key: 'Start', label: 'Start', icon: 'chess-king'},
                  {key: 'FEN', label: 'FEN', icon: 'checkerboard'},
                  {key: 'Editor', label: 'Editor', icon: 'chess-pawn'},
                  {key: 'PGN', label: 'PGN', icon: 'file-document-outline'},
                ].map(item => {
                  const isActive = typeKey === item.key;
                  return (
                    <TouchableOpacity
                      key={item.key}
                      activeOpacity={0.8}
                      onPress={() => {
                        setNewChapterDetails(prev => ({
                          ...prev,
                          types: {key: item.key as any, value: item.key as any},
                        }));
                        if (item.key === 'Editor') setIsBoardEditorVisible(true);
                      }}
                      style={{
                        flex: 1,
                        borderRadius: 10,
                        borderWidth: 1,
                        borderColor: isActive ? '#B58E58' : '#E6E6E6',
                        backgroundColor: isActive ? '#FFFDF8' : '#FFFFFF',
                        alignItems: 'center',
                        justifyContent: 'center',
                        paddingVertical: 8,
                        gap: 4,
                      }}>
                      <MaterialCommunityIcons name={item.icon} size={15} color={isActive ? '#B58E58' : '#757575'} />
                      <Text style={{fontSize: 10, fontWeight: 'bold', color: isActive ? '#B58E58' : '#757575'}}>{item.label}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>

            {/* FEN/PGN Input box if selected */}
            {typeKey === 'FEN' && (
              <View style={{gap: 6}}>
                <Text style={{fontSize: 10, fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>FEN STRING</Text>
                <TextInput
                  value={newChapterDetails?.fen}
                  placeholder="rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
                  placeholderTextColor="#A39081"
                  onChangeText={text => {
                    setNewChapterDetails(prev => ({...prev, fen: text}));
                    if (text) {
                      const check = validateFen(text);
                      setError(check.ok ? {flag: false, errorMsg: ''} : {flag: true, errorMsg: check.error});
                    } else {
                      setError({flag: false, errorMsg: ''});
                    }
                  }}
                  style={{
                    height: 38,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: error?.flag ? '#FF4D4D' : '#E6E6E6',
                    backgroundColor: '#FFFFFF',
                    paddingHorizontal: 12,
                    fontSize: 11,
                    color: '#1E110A',
                  }}
                />
                {error?.flag && (
                  <Text style={{fontSize: 9, color: '#FF4D4D', fontWeight: '500'}}>{error?.errorMsg}</Text>
                )}
              </View>
            )}

            {typeKey === 'PGN' && (
              <View style={{gap: 6}}>
                <Text style={{fontSize: 10, fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>PGN CONTENT</Text>
                <TextInput
                  value={newChapterDetails?.pgn}
                  placeholder="1. e4 e5 2. Nf3 Nc6..."
                  placeholderTextColor="#A39081"
                  multiline
                  numberOfLines={3}
                  onChangeText={text => {
                    setNewChapterDetails(prev => ({...prev, pgn: text}));
                    setError({flag: false, errorMsg: ''});
                  }}
                  style={{
                    height: 60,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: '#E6E6E6',
                    backgroundColor: '#FFFFFF',
                    paddingHorizontal: 12,
                    paddingVertical: 8,
                    fontSize: 11,
                    color: '#1E110A',
                  }}
                />
              </View>
            )}
          </View>

          {/* Divider */}
          <View style={{width: 1, backgroundColor: '#E6E6E6'}} />

          {/* Right Column: Preview Board */}
          <View style={{width: 230, backgroundColor: '#FFFDF6', padding: 20, alignItems: 'center', justifyContent: 'center', gap: 10}}>
            <Text style={{fontSize: 10, fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>PREVIEW</Text>
            
            <View style={{width: 170, height: 170, borderRadius: 8, overflow: 'hidden', borderWidth: 1, borderColor: '#EAE2D5'}}>
              <Chessboard
                position={typeKey === 'FEN' || typeKey === 'Editor' ? newChapterDetails?.fen || START_FEN : START_FEN}
                boardWidth={170}
                customLightSquareStyle={{backgroundColor: '#edd899'}}
                customDarkSquareStyle={{backgroundColor: '#d1ab41'}}
                arePiecesDraggable={false}
              />
            </View>

            <Text style={{fontSize: 10, color: '#999999', marginTop: 4}}>{previewDescription}</Text>
          </View>
        </View>

        {/* Footer */}
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'flex-end',
            gap: 10,
            paddingHorizontal: 24,
            paddingBottom: 20,
            paddingTop: 10,
            borderTopWidth: 1,
            borderColor: '#F5F5F5',
          }}>
          <TouchableOpacity
            onPress={resetStates}
            activeOpacity={0.8}
            style={{
              borderWidth: 1.5,
              borderColor: '#CCCCCC',
              backgroundColor: '#FFFFFF',
              borderRadius: 100,
              paddingHorizontal: 22,
              paddingVertical: 8,
            }}>
            <Text style={{fontSize: 12, fontWeight: 'bold', color: '#2E1B10'}}>Cancel</Text>
          </TouchableOpacity>

          <TouchableOpacity
            disabled={!canSubmit}
            onPress={handleFenSubmit}
            activeOpacity={0.8}
            style={{
              backgroundColor: canSubmit ? '#FACF47' : '#F5F5F5',
              borderRadius: 100,
              paddingHorizontal: 24,
              paddingVertical: 8,
            }}>
            <Text style={{fontSize: 12, fontWeight: 'bold', color: canSubmit ? '#1E110A' : '#B0B0B0'}}>
              {createChapterOnly ? 'Create Chapter' : 'Create Workbook'}
            </Text>
          </TouchableOpacity>
        </View>
      </SlideModal>

      {/* ── Popup 2: Board Editor Modal ─────────────────────────────────────── */}
      <SlideModal
        visible={isBoardEditorVisible}
        onClose={() => setIsBoardEditorVisible(false)}
        type="center"
        showHeader={false}
        contentPadding={0}
        customStyle={{
          borderRadius: 20,
          overflow: 'hidden',
          backgroundColor: '#FFFFFF',
          width: 660,
          maxWidth: '92%',
          alignSelf: 'center',
          borderWidth: 0,
          shadowColor: '#000',
          shadowOffset: {width: 0, height: 10},
          shadowOpacity: 0.08,
          shadowRadius: 20,
          elevation: 5,
        }}>
        
        {/* Header Block */}
        <View
          style={{
            backgroundColor: '#2C1A11',
            paddingHorizontal: 24,
            paddingVertical: 18,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
          }}>
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, marginRight: 24}}>
            <View
              style={{
                width: 38,
                height: 38,
                borderRadius: 8,
                backgroundColor: '#402C1F',
                borderWidth: 1,
                borderColor: '#B58E58',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <MaterialCommunityIcons name="chess-bishop" size={18} color="#FFFFFF" />
            </View>
            <View style={{flex: 1}}>
              <Text style={{fontSize: 15, fontWeight: 'bold', color: '#FFFFFF', letterSpacing: -0.2}}>
                Board Editor
              </Text>
              <Text style={{fontSize: 11, color: '#A39081', marginTop: 1}}>
                Click a piece, then click a square to place it.
              </Text>
            </View>
          </View>

          {/* Close button */}
          <TouchableOpacity
            onPress={() => setIsBoardEditorVisible(false)}
            activeOpacity={0.8}
            style={{
              width: 28,
              height: 28,
              borderRadius: 14,
              backgroundColor: 'rgba(255,255,255,0.08)',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Ionicons name="close-outline" size={16} color="#FFFFFF" />
          </TouchableOpacity>
        </View>

        <BoardEditor
          initialFen={newChapterDetails?.fen}
          onApply={fen => {
            setNewChapterDetails(prev => ({...prev, fen}));
            setIsBoardEditorVisible(false);
          }}
          onClose={() => setIsBoardEditorVisible(false)}
        />
      </SlideModal>

      {/* Start Button Trigger */}
      <View style={{flexDirection: 'row', justifyContent: 'flex-end', marginTop: 10}}>
        {overrideButton ? (
          overrideButton(() => setOpenNewStudyModal(true))
        ) : (
          <TouchableOpacity
            activeOpacity={0.8}
            onPress={() => {
              setOpenNewStudyModal(true);
              setIsStudyModalOpen(true);
            }}
            style={{
              backgroundColor: CCColors.Primary.Action,
              paddingHorizontal: 16,
              paddingVertical: 10,
              borderRadius: 8,
            }}>
            <Text style={{fontSize: 13, fontWeight: 'bold', color: CCColors.Primary.Dark}}>
              + Start new workbook
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </>
  );
}

export default CreateNewStudy;
